import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from '../utils/axiosConfig';
import { getActionErrorMessage } from '../utils/errorHandler';
import { FaLock, FaEye, FaEyeSlash, FaCheck, FaTimes } from "react-icons/fa";
import "../styles/ChangePassword.css";
import { useToast } from '../utils/toastContext';

const ChangePassword = ({ embedded = false }) => {
  const { showError, showSuccess } = useToast();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();

  // Password validation states
  const [passwordChecks, setPasswordChecks] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    digit: false,
    special: false,
    different: false
  });

  const validatePassword = (password) => {
    const checks = {
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      digit: /\d/.test(password),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(password),
      different: password !== currentPassword && password.length > 0
    };
    
    setPasswordChecks(checks);
    return Object.values(checks).every(check => check);
  };

  const handleCurrentPasswordChange = (e) => {
    setCurrentPassword(e.target.value);
    setError("");
    // Re-validate new password for "different" check
    if (newPassword) {
      validatePassword(newPassword);
    }
  };

  const handleNewPasswordChange = (e) => {
    const value = e.target.value;
    setNewPassword(value);
    validatePassword(value);
    setError("");
  };

  const handleConfirmPasswordChange = (e) => {
    setConfirmPassword(e.target.value);
    setError("");
  };

  const handleChangePassword = async (event) => {
    event.preventDefault();
    setError("");
    setSuccess("");

    // Validate current password is provided
    if (!currentPassword.trim()) {
      setError("Current password is required.");
      return;
    }

    // Validate passwords match
    if (newPassword !== confirmPassword) {
      setError("New password and confirmation password do not match.");
      return;
    }

    // Validate password strength
    if (!validatePassword(newPassword)) {
      setError("Please ensure all password requirements are met.");
      return;
    }

    setIsLoading(true);

    try {
      const response = await axiosInstance.post("/auth/change-password", {
        current_password: currentPassword,
        new_password: newPassword,
        confirm_password: confirmPassword
      });

      if (response.data.status === "success") {
        setSuccess("Password changed successfully! Redirecting...");
        // Clear form
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");
        setPasswordChecks({
          length: false,
          uppercase: false,
          lowercase: false,
          digit: false,
          special: false,
          different: false
        });
        // Redirect after a short delay
        setTimeout(() => {
          navigate(-1);
        }, 2000);
      }
    } catch (err) {
      console.error(err);
      // Use sanitized error message - never show raw backend errors
      setError(getActionErrorMessage(err, "change password"));
    } finally {
      setIsLoading(false);
    }
  };

  const isFormValid = () => {
    return currentPassword.trim() && 
           newPassword && 
           confirmPassword && 
           newPassword === confirmPassword &&
           Object.values(passwordChecks).every(check => check);
  };

  const passwordRules = [
    { key: 'length', label: 'At least 8 characters long', check: passwordChecks.length },
    { key: 'uppercase', label: 'Contains uppercase letter', check: passwordChecks.uppercase },
    { key: 'lowercase', label: 'Contains lowercase letter', check: passwordChecks.lowercase },
    { key: 'digit', label: 'Contains number', check: passwordChecks.digit },
    { key: 'special', label: 'Contains special character', check: passwordChecks.special },
    { key: 'different', label: 'Different from current password', check: passwordChecks.different }
  ];

  React.useEffect(() => {
    if (error) {
      showError(error);
    }
  }, [error, showError]);

  React.useEffect(() => {
    if (success) {
      showSuccess(success);
    }
  }, [success, showSuccess]);

  return (
    <div className={`change-password-shell ${embedded ? 'embedded' : ''}`}>
      <div className="change-password-card">
        <div className="change-password-header">
          <div className="change-password-lock-wrap">
            <FaLock />
          </div>
          <h1 className="change-password-title">Change Password</h1>
          <p className="change-password-subtitle">Update your password to keep your account secure</p>
        </div>

        <div className="change-password-body">
          <form onSubmit={handleChangePassword}>
            <div className="change-field">
              <label className="change-password-label">
                <FaLock />
                Current Password
              </label>
              <div className="change-password-input-wrap">
                <input
                  type={showCurrentPassword ? "text" : "password"}
                  value={currentPassword}
                  onChange={handleCurrentPasswordChange}
                  placeholder="Enter your current password"
                  required
                  className="change-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword((v) => !v)}
                  className="change-password-eye"
                >
                  {showCurrentPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
            </div>
            
            <div className="change-field">
              <label className="change-password-label">
                <FaLock />
                New Password
              </label>
              <div className="change-password-input-wrap">
                <input
                  type={showNewPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={handleNewPasswordChange}
                  placeholder="Enter new password"
                  required
                  className="change-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword((v) => !v)}
                  className="change-password-eye"
                >
                  {showNewPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
            </div>
            
            <div className="change-field">
              <label className="change-password-label">
                <FaLock />
                Confirm New Password
              </label>
              <div className="change-password-input-wrap">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={handleConfirmPasswordChange}
                  placeholder="Confirm new password"
                  required
                  className="change-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword((v) => !v)}
                  className="change-password-eye"
                >
                  {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
              {confirmPassword && newPassword !== confirmPassword && (
                <div className="change-password-warning">
                  <FaTimes />
                  <p>
                    Passwords do not match
                  </p>
                </div>
              )}
            </div>
            
            <div className="change-password-rules">
              <h4>Password Requirements</h4>
              <div className="change-password-rule-grid">
                {passwordRules.map(({ key, label, check }) => (
                  <div key={key} className={`change-password-rule ${check ? 'is-valid' : ''}`}>
                    <span className="change-password-rule-icon">{check ? <FaCheck /> : <FaTimes />}</span>
                    <span>{label}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <button 
              type="submit" 
              disabled={isLoading || !isFormValid()}
              className="change-password-submit"
            >
              {isLoading ? (
                <div className="change-password-submit-loading">
                  <div className="change-password-spinner"></div>
                  Changing Password...
                </div>
              ) : (
                "Change Password"
              )}
            </button>
            
          </form>
        </div>
      </div>
    </div>
  );
};

export default ChangePassword; 
